import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useCountry } from "../contexts/CountryContext";
import { useAuth } from "../contexts/AuthContext";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import { track } from "../lib/analytics";
import { renderMarkdown } from "../lib/markdown";
import { formatPrice } from "../components/shop/ProductCardV2";
import ProductGallery from "../components/shop/ProductGallery";
import FaqAccordion from "../components/shop/FaqAccordion";
import PdfPreviewCard from "../components/shop/PdfPreviewCard";
import { BundleSuggestions, FrequentlyBoughtTogetherList } from "../components/shop/BundleSuggestions";
import PreCheckoutModal from "../components/shop/PreCheckoutModal";
import StickyWhatsAppButton from "../components/shop/StickyWhatsAppButton";
import CountryPill from "../components/shop/CountryPill";
import TrustStrip from "../components/shop/TrustStrip";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  ArrowLeft, Sparkles, ShieldCheck, MessageCircle, HeartHandshake,
  Leaf, Check, ArrowRight, Loader2, AlertTriangle, Truck, Award,
  HelpCircle, BookOpen,
} from "lucide-react";

/**
 * /shop/:slug — public product detail page.
 *
 * Concierge feel:
 *   - Hero (gallery + headline + 2 calm CTAs)
 *   - Trust ribbon (refunds, guidance, science)
 *   - "Who this is best for" trust layer
 *   - Tabs: Description / Ingredients / How to use / FAQ
 *   - Embedded PDF preview cards (ingredient sheet / fact sheet etc.)
 *   - Bundles + Frequently bought together
 *   - Talk-to-a-coach CTA
 *
 * Country-aware via CountryContext. Schema.org `Product` JSON-LD baked
 * in via `useSeo()`.
 */
export default function ShopProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const ctx = useCountry();
  const { user } = useAuth() || {};
  const country = ctx?.country || "UK";
  const currency = ctx?.currency || "GBP";

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState({}); // slug -> resolved product
  const [showCheckout, setShowCheckout] = useState(false);

  // Fetch product when slug or country changes
  useEffect(() => {
    if (!ctx?.hydrated) return;
    let cancelled = false;
    setLoading(true);
    setProduct(null);
    setError(null);
    api
      .get(`/v2/products/${slug}?country=${encodeURIComponent(country)}`)
      .then(({ data }) => {
        if (!cancelled) {
          setProduct(data);
          try { track?.event?.("shop_product_view", { slug: data.slug, name: data.name, country, currency: data.currency, price: data.price }); } catch { /* ignore */ }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const code = e?.response?.status;
        setError(code === 404 ? "not_available" : (e?.response?.data?.detail || "Failed to load product"));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, country, ctx?.hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch resolved bundle / FBT items
  useEffect(() => {
    const slugs = [
      ...(product?.bundle_with || []),
      ...(product?.frequently_bought_with || []),
    ];
    if (slugs.length === 0) { setRelated({}); return; }
    let cancelled = false;
    Promise.all(
      slugs.map((s) => api.get(`/v2/products/${s}?country=${encodeURIComponent(country)}`).then((r) => [s, r.data]).catch(() => [s, null])),
    ).then((pairs) => {
      if (cancelled) return;
      const map = {};
      for (const [s, p] of pairs) if (p) map[s] = p;
      setRelated(map);
    });
    return () => { cancelled = true; };
  }, [product, country]);

  // SEO + JSON-LD
  useSeo(
    product
      ? {
          title: `${product.name} — Feel Greats`,
          description: product.short_description || `${product.name} — guided wellness from Feel Greats.`,
          canonical: `${PROD_DOMAIN}/shop/${product.slug}`,
          ogType: "product",
          ogImage: product.hero_image?.url || undefined,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "description": product.short_description || product.description_md?.slice(0, 280) || "",
              "image": [product.hero_image?.url, ...(product.gallery || []).map((g) => g.url)].filter(Boolean),
              "brand": { "@type": "Brand", "name": "Unicity (curated by Feel Greats)" },
              "category": product.category,
              "offers": {
                "@type": "Offer",
                "url": `${PROD_DOMAIN}/shop/${product.slug}`,
                "priceCurrency": product.currency,
                "price": product.price,
                "availability": "https://schema.org/InStock",
                "areaServed": product.country,
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${PROD_DOMAIN}/` },
                { "@type": "ListItem", "position": 2, "name": "Shop", "item": `${PROD_DOMAIN}/shop` },
                { "@type": "ListItem", "position": 3, "name": product.name, "item": `${PROD_DOMAIN}/shop/${product.slug}` },
              ],
            },
          ],
        }
      : {
          title: "Loading product — Feel Greats",
          canonical: `${PROD_DOMAIN}/shop`,
        },
  );

  const bundleResolved = useMemo(() => (product?.bundle_with || []).map((s) => related[s]).filter(Boolean), [product, related]);
  const fbtResolved = useMemo(() => (product?.frequently_bought_with || []).map((s) => related[s]).filter(Boolean), [product, related]);

  // ───────────── LOADING / ERROR STATES ─────────────
  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="shop-product-loading">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-700/70" />
      </main>
    );
  }
  if (error === "not_available") {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="shop-product-not-available">
        <div className="rounded-3xl border border-amber-200 bg-amber-50/50 p-10 text-center">
          <AlertTriangle className="w-9 h-9 mx-auto text-amber-700/80 mb-3" strokeWidth={1.4} />
          <h1 className="font-display text-3xl text-emerald-950 font-light">Not available in {country} yet</h1>
          <p className="text-sm text-emerald-900/75 mt-3 max-w-md mx-auto leading-relaxed">
            We don't currently offer this product in your country. Try changing country or chat to our coach for a personal suggestion.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <CountryPill variant="full" />
            <Link to="/shop"><Button variant="outline" className="rounded-full">Back to shop</Button></Link>
          </div>
        </div>
      </main>
    );
  }
  if (error || !product) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="shop-product-error">
        <p className="text-emerald-900/80">{error || "Product not found."}</p>
        <Link to="/shop" className="text-emerald-700 hover:text-emerald-800 mt-3 inline-flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to shop
        </Link>
      </main>
    );
  }

  // ───────────── HAPPY PATH ─────────────
  const purchaseUrl = product.purchase_link || "/contact?intent=mentor";
  const hasSubscription = product.allows_subscription && product.subscription_price > 0 && product.subscription_price < product.price;
  const trackedPurchaseClick = () => {
    try {
      track?.event?.("shop_purchase_click", {
        slug: product.slug,
        name: product.name,
        country: product.country,
        currency: product.currency,
        price: product.price,
        signed_in: !!user,
      });
    } catch { /* ignore */ }
  };

  return (
    <main className="bg-gradient-to-b from-emerald-50/30 via-white to-white pb-12 overflow-x-hidden" data-testid={`shop-product-page-${product.slug}`}>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 flex items-center justify-between flex-wrap gap-3">
        <nav className="text-xs text-emerald-900/55" aria-label="Breadcrumb" data-testid="shop-product-breadcrumb">
          <Link to="/" className="hover:text-emerald-700">Home</Link>
          <span aria-hidden> / </span>
          <Link to="/shop" className="hover:text-emerald-700">Shop</Link>
          <span aria-hidden> / </span>
          <span className="text-emerald-900/80 font-medium">{product.name}</span>
        </nav>
        <CountryPill variant="compact" />
      </div>

      {/* ───────────── HERO ───────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-start">
          {/* Gallery */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <ProductGallery hero={product.hero_image} gallery={product.gallery} productName={product.name} />
          </motion.div>

          {/* Copy + CTAs */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-700 mb-2">
              {(product.category || "supplement").replace(/_/g, " ")}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-emerald-950 font-light leading-[1.08] tracking-tight" data-testid="shop-product-name">
              {product.name}
            </h1>
            {product.short_description && (
              <p className="text-lg text-emerald-900/75 mt-3 leading-relaxed" data-testid="shop-product-tagline">
                {product.short_description}
              </p>
            )}

            {/* Best-for chips (trust layer) */}
            {(product.best_for || []).length > 0 && (
              <div className="mt-5 rounded-2xl bg-emerald-50/50 border border-emerald-100 p-4" data-testid="shop-product-best-for">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700">Who this is best for</p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {(product.best_for || []).map((b) => (
                    <li key={b} className="inline-flex items-center text-[12px] font-medium text-emerald-900 bg-white border border-emerald-100 rounded-full px-2.5 py-1">
                      <Check className="w-3 h-3 text-emerald-700 mr-1" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits — quick wins */}
            {(product.benefits || []).length > 0 && (
              <ul className="mt-5 space-y-2" data-testid="shop-product-benefits">
                {product.benefits.slice(0, 5).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-emerald-900">
                    <Leaf className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" strokeWidth={1.6} /> {b}
                  </li>
                ))}
              </ul>
            )}

            {/* Pricing block */}
            <div className="mt-6 rounded-2xl border border-emerald-100 bg-white p-5" data-testid="shop-product-price-block">
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">One-time</p>
                  <p className="font-display text-3xl sm:text-4xl text-emerald-950 leading-none mt-0.5">
                    {formatPrice(product.price, product.currency)}
                  </p>
                </div>
                {hasSubscription && (
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">Subscribe & save</p>
                    <p className="font-display text-xl text-emerald-950 leading-none mt-0.5">
                      {formatPrice(product.subscription_price, product.currency)} <span className="text-[12px] text-emerald-900/65">/ month</span>
                    </p>
                    <p className="text-[11px] text-emerald-700/80 mt-1">Save {product.discount_percent || 10}% — cancel anytime</p>
                  </div>
                )}
              </div>

              {/* Primary CTAs */}
              <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
                <Button
                  className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-6 px-6 text-base flex-1"
                  data-testid="shop-product-buy-btn"
                  onClick={() => { trackedPurchaseClick(); setShowCheckout(true); }}
                >
                  Continue to checkout <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Link to="/questionnaire" className="flex-1" data-testid="shop-product-assess-btn">
                  <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 py-6 px-6 text-base w-full">
                    <Sparkles className="w-4 h-4 mr-1.5" /> Take assessment first
                  </Button>
                </Link>
              </div>

              {/* Secondary "talk to coach" */}
              <a
                href="https://wa.me/447424225333?text=Hi%2C%20I%27d%20like%20to%20chat%20about%20a%20product"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-[12px] text-emerald-700 hover:text-emerald-800 inline-flex items-center"
                data-testid="shop-product-whatsapp-link"
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> Or chat to a coach on WhatsApp first
              </a>
            </div>

            {/* Trust ribbon */}
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[11px]" data-testid="shop-product-trust-ribbon">
              <TrustRibbonCell icon={ShieldCheck} title="Refunds" body="Transparent policy" />
              <TrustRibbonCell icon={Truck} title="Direct" body="Ships from Unicity" />
              <TrustRibbonCell icon={Award} title="Backed" body="35 yrs of research" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────────── TABS BLOCK ───────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="shop-product-tabs">
        <Tabs defaultValue="description">
          <TabsList className="flex w-full overflow-x-auto bg-emerald-50/50 rounded-full p-1 max-w-full">
            <TabsTrigger value="description" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 text-sm" data-testid="shop-tab-description">Description</TabsTrigger>
            <TabsTrigger value="ingredients" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 text-sm" data-testid="shop-tab-ingredients">Ingredients</TabsTrigger>
            <TabsTrigger value="usage" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 text-sm" data-testid="shop-tab-usage">How to use</TabsTrigger>
            <TabsTrigger value="faq" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-5 text-sm" data-testid="shop-tab-faq">FAQ</TabsTrigger>
          </TabsList>

          <TabsContent value="description" className="mt-5">
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
              <article className="rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8 prose-emerald" data-testid="shop-tabpanel-description">
                {product.description_md ? renderMarkdown(product.description_md) : (
                  <p className="text-emerald-900/75">No description provided yet — chat to our coach for a guided overview.</p>
                )}
              </article>
              <GuidanceCard />
            </div>
          </TabsContent>

          <TabsContent value="ingredients" className="mt-5">
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
              <article className="rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8" data-testid="shop-tabpanel-ingredients">
                {product.ingredients_md ? renderMarkdown(product.ingredients_md) : (
                  <p className="text-emerald-900/75 text-sm">Full ingredient breakdown is in the linked PDF below.</p>
                )}
                {(product.pdfs || []).filter((p) => p.kind === "ingredients").length > 0 && (
                  <div className="mt-5 grid gap-2.5">
                    {product.pdfs.filter((p) => p.kind === "ingredients").map((p, idx) => (
                      <PdfPreviewCard key={p.url + idx} pdf={p} />
                    ))}
                  </div>
                )}
              </article>
              <SafetyCard />
            </div>
          </TabsContent>

          <TabsContent value="usage" className="mt-5">
            <div className="rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8" data-testid="shop-tabpanel-usage">
              {product.usage_md ? renderMarkdown(product.usage_md) : (
                <p className="text-emerald-900/75 text-sm">A short usage guide is included with the product. Reach out to our coach if you have questions about timing or pairing.</p>
              )}
              {(product.pdfs || []).filter((p) => p.kind === "usage_guide").length > 0 && (
                <div className="mt-5 grid gap-2.5">
                  {product.pdfs.filter((p) => p.kind === "usage_guide").map((p, idx) => (
                    <PdfPreviewCard key={p.url + idx} pdf={p} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="faq" className="mt-5">
            {(product.faq || []).length > 0 ? (
              <FaqAccordion items={product.faq} />
            ) : (
              <div className="rounded-3xl border border-emerald-100 bg-white p-6 text-sm text-emerald-900/75" data-testid="shop-tabpanel-faq-empty">
                No FAQs added yet — chat to our coach with any question.
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>

      {/* ───────────── PDF Library (all non-ingredient/non-usage PDFs) ───────────── */}
      {((product.pdfs || []).filter((p) => !["ingredients", "usage_guide"].includes(p.kind)).length > 0) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="rounded-3xl border border-emerald-100 bg-white p-6 sm:p-8" data-testid="shop-product-pdf-library">
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Documents</p>
            <h3 className="font-display text-xl sm:text-2xl text-emerald-950 font-light mt-1">Read at your pace.</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {product.pdfs.filter((p) => !["ingredients", "usage_guide"].includes(p.kind)).map((p, idx) => (
                <PdfPreviewCard key={p.url + idx} pdf={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ───────────── BUNDLES + FBT ───────────── */}
      {(bundleResolved.length > 0 || fbtResolved.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <BundleSuggestions items={bundleResolved} />
          <FrequentlyBoughtTogetherList items={fbtResolved} />
        </section>
      )}

      {/* ───────────── Reassurance row ───────────── */}
      <TrustStrip variant="compact" />

      {/* ───────────── Pre-checkout modal ───────────── */}
      <PreCheckoutModal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        product={product}
        purchaseUrl={purchaseUrl}
      />

      {/* Sticky coach */}
      <StickyWhatsAppButton message={`Hi! I'd like guidance on ${product.name}.`} />
    </main>
  );
}


// ============================================================================
//  Small helper UI atoms
// ============================================================================
function TrustRibbonCell({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-emerald-100 bg-white p-2.5">
      <Icon className="w-4 h-4 text-emerald-700 mx-auto mb-1" strokeWidth={1.6} />
      <div className="text-[11px] font-semibold text-emerald-950">{title}</div>
      <div className="text-[10px] text-emerald-900/55 leading-tight">{body}</div>
    </div>
  );
}

function GuidanceCard() {
  return (
    <aside className="rounded-3xl border border-amber-200/60 bg-amber-50/40 p-6 sm:p-7 h-fit sticky top-24" data-testid="shop-product-guidance-card">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white text-amber-700 mb-3 border border-amber-200">
        <HelpCircle className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">Real guidance</p>
      <h3 className="font-display text-xl text-emerald-950 font-light leading-snug mt-1">
        Not sure if this is for you?
      </h3>
      <p className="text-[13px] text-emerald-900/75 mt-2 leading-relaxed">
        We're a wellness team first. Tell us a little about you and we'll suggest the right path — sometimes that's a different product, sometimes it's a habit.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <Link to="/questionnaire">
          <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white w-full text-sm">
            <Sparkles className="w-4 h-4 mr-1.5" /> Take free assessment
          </Button>
        </Link>
        <Link to="/contact?intent=mentor">
          <Button variant="outline" className="rounded-full border-emerald-300 text-emerald-900 hover:bg-emerald-50 w-full text-sm">
            <HeartHandshake className="w-4 h-4 mr-1.5" /> Book a 1:1 mentor
          </Button>
        </Link>
        <Link to="/learn" className="text-[12px] text-emerald-700 hover:text-emerald-800 inline-flex items-center justify-center mt-1">
          <BookOpen className="w-3.5 h-3.5 mr-1" /> Read the wellness library
        </Link>
      </div>
    </aside>
  );
}

function SafetyCard() {
  return (
    <aside className="rounded-3xl border border-emerald-100 bg-emerald-50/40 p-6 sm:p-7 h-fit" data-testid="shop-product-safety-card">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white text-emerald-700 mb-3 border border-emerald-100">
        <ShieldCheck className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Safety first</p>
      <h3 className="font-display text-xl text-emerald-950 font-light leading-snug mt-1">
        Wellness, not medical advice.
      </h3>
      <p className="text-[13px] text-emerald-900/75 mt-2 leading-relaxed">
        Always check with a clinician if you're pregnant, nursing, taking medication or managing a chronic condition. Results vary, and small steps stack up.
      </p>
    </aside>
  );
}
